import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Stack } from '@nike/podium';
import { Skeleton } from '~/components/shadcn/skeleton';
import { Badge } from '~/components/shadcn/badge';
import { usePreferredImageIndex } from '~/hooks/usePreferredImageIndex';
const IMAGE_DIMENSIONS = {
    MAIN_MAX_HEIGHT: 400,
    THUMBNAIL_SIZE: 50
};
const mockThumbnails = Array(15).fill({
    url: '/assets/mockProductMain.png',
    alt: 'Nike Product Mock'
});
const mockMainImages = [
    {
        url: '/assets/mockProductMain.png',
        alt: 'Nike Product Mock'
    }
];
function EmptyImagePlaceholder() {
    return (_jsx("div", { className: "flex items-center justify-center bg-gray-100 rounded w-full", style: {
            maxHeight: IMAGE_DIMENSIONS.MAIN_MAX_HEIGHT
        }, children: _jsxs("div", { className: "flex flex-col items-center gap-4 py-24", children: [_jsx("img", { src: "/assets/swoosh/black.svg", alt: "No image available", className: "w-24 h-24 opacity-20" }), _jsx("p", { className: "text-sm text-gray-500", children: "No images available" })] }) }));
}
function ImageThumbnail({ image, index, isSelected, onSelect }) {
    const thumbnailPadding = 8;
    return (_jsxs("button", { onMouseEnter: () => onSelect(index), onClick: () => onSelect(index), className: "flex-shrink-0 overflow-hidden relative group rounded cursor-pointer flex items-center justify-center", style: {
            width: IMAGE_DIMENSIONS.THUMBNAIL_SIZE,
            height: IMAGE_DIMENSIONS.THUMBNAIL_SIZE,
            backgroundColor: 'color-mix(in srgb, var(--pds-color-background-image) 40%, white)',
            padding: thumbnailPadding
        }, children: [image.url ? (_jsx("img", { src: image.url, alt: image.alt || `Thumbnail ${index + 1}`, className: "w-full h-full object-contain" })) : (_jsx(Skeleton, { className: "w-6 h-6 rounded-none" })), _jsx("div", { className: `absolute inset-0 transition-opacity pointer-events-none rounded ${isSelected ? 'opacity-40' : 'opacity-0 group-hover:opacity-20'}`, style: {
                    backgroundColor: 'var(--pds-color-background-scrim)'
                } })] }));
}
/**
 * ImageGallery - Reusable image gallery component for entity detail sidebars
 *
 * Features:
 * - Large primary image display (top)
 * - Horizontal thumbnail navigation (bottom) when multiple images exist
 * - Hover and click to select different images
 * - Remembers user's preferred image view per entity
 * - Loading skeletons for missing images
 */
function ImageGallery({ images, entityId, badges = [] }) {
    const [imageError, setImageError] = useState(false);
    const hasRealImages = images && images.length > 0;
    useEffect(() => {
        setImageError(false);
    }, [entityId]);
    const thumbnailImages = hasRealImages ? images : mockThumbnails;
    const mainImage = hasRealImages ? images[0] : mockMainImages[0];
    const [selectedImageIndex, setSelectedImageIndex] = usePreferredImageIndex(thumbnailImages, [entityId]);
    const selectedImage = thumbnailImages[selectedImageIndex] || mainImage || mockMainImages[0];
    const hasThumbnails = thumbnailImages.length > 1;
    const displayImage = selectedImage?.url ? selectedImage : mockMainImages[0];
    if (!displayImage || imageError) {
        return _jsx(EmptyImagePlaceholder, {});
    }
    const leftBadges = badges.filter((b) => b.position === 'left');
    const rightBadges = badges.filter((b) => b.position === 'right');
    const getBadgeClassName = (variant) => {
        const base = 'px-3 py-1.5 bg-[rgba(158,158,160,0.2)] pds-color-text-secondary hover:bg-[rgba(158,158,160,0.3)] text-xs';
        return variant === 'pill' ? `${base} rounded-full` : base;
    };
    const getBadgeStyle = (variant) => {
        return variant === 'square' ? { borderRadius: '4px' } : undefined;
    };
    return (_jsxs(Stack, { gap: "m", className: "w-full", children: [badges.length > 0 && (_jsxs("div", { className: "w-full flex items-center justify-between", children: [_jsx("div", { className: "flex gap-2", children: leftBadges.map((badge, index) => (_jsx(Badge, { variant: "secondary", className: getBadgeClassName(badge.variant), style: getBadgeStyle(badge.variant), children: badge.text }, `left-${index}`))) }), _jsx("div", { className: "flex gap-2", children: rightBadges.map((badge, index) => (_jsx(Badge, { variant: "secondary", className: getBadgeClassName(badge.variant), style: getBadgeStyle(badge.variant), children: badge.text }, `right-${index}`))) })] })), _jsx("div", { className: "w-full flex items-center justify-center bg-gray-100 overflow-hidden", style: {
                    maxHeight: IMAGE_DIMENSIONS.MAIN_MAX_HEIGHT,
                    borderRadius: 'var(--pds-radius-8)'
                }, children: _jsx("img", { src: displayImage.url, alt: displayImage.alt || 'Entity image', className: "w-full h-auto object-contain", style: {
                        maxHeight: IMAGE_DIMENSIONS.MAIN_MAX_HEIGHT
                    }, onError: () => setImageError(true) }) }), hasThumbnails && (_jsx("div", { className: "w-full overflow-x-auto thumbnail-scrollbar", children: _jsx(Stack, { horizontal: true, gap: "s", className: "w-max", children: thumbnailImages.map((image, index) => (_jsx(ImageThumbnail, { image: image, index: index, isSelected: selectedImageIndex === index, onSelect: setSelectedImageIndex }, index))) }) }))] }));
}
export default ImageGallery;
